export class DebugConsole {
    constructor(game) {
        this.game = game;
        this.isVisible = false;
        this.commandHistory = [];
        this.historyIndex = -1;
        this.godMode = false;
        
        this.createConsoleUI();
        this.setupEventListeners();
        this.registerCommands();
    }
    
    createConsoleUI() {
        // Console container
        this.container = document.createElement('div');
        this.container.id = 'debug-console';
        this.container.style.cssText = `
            position: fixed;
            top: 50px;
            left: 50%;
            transform: translateX(-50%);
            width: 700px;
            max-height: 400px;
            background: rgba(0, 0, 0, 0.95);
            border: 2px solid #00ff00;
            border-radius: 8px;
            font-family: 'Courier New', monospace;
            font-size: 14px;
            color: #00ff00;
            display: none;
            z-index: 10000;
            padding: 10px;
            box-shadow: 0 0 20px rgba(0, 255, 0, 0.5);
        `;
        
        // Output area
        this.output = document.createElement('div');
        this.output.style.cssText = `
            height: 300px;
            overflow-y: auto;
            margin-bottom: 10px;
            padding: 5px;
            border-bottom: 1px solid #00ff00;
        `;
        this.container.appendChild(this.output);
        
        // Input area
        const inputContainer = document.createElement('div');
        inputContainer.style.cssText = 'display: flex; align-items: center;';
        
        const prompt = document.createElement('span');
        prompt.textContent = '> ';
        prompt.style.marginRight = '5px';
        inputContainer.appendChild(prompt);
        
        this.input = document.createElement('input');
        this.input.type = 'text';
        this.input.style.cssText = `
            flex: 1;
            background: transparent;
            border: none;
            color: #00ff00;
            font-family: 'Courier New', monospace;
            font-size: 14px;
            outline: none;
        `;
        inputContainer.appendChild(this.input);
        this.container.appendChild(inputContainer);
        
        document.body.appendChild(this.container);
        
        this.log('Debug Console initialized. Type "help" for commands.');
    }
    
    setupEventListeners() {
        // Toggle console with ~
        document.addEventListener('keydown', (e) => {
            if (e.key === '`' || e.key === '~') {
                e.preventDefault();
                this.toggle();
            }
        });
        
        // Command submission
        this.input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                const command = this.input.value.trim();
                if (command) {
                    this.executeCommand(command);
                    this.commandHistory.push(command);
                    this.historyIndex = this.commandHistory.length;
                    this.input.value = '';
                }
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                if (this.historyIndex > 0) {
                    this.historyIndex--;
                    this.input.value = this.commandHistory[this.historyIndex];
                }
            } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                if (this.historyIndex < this.commandHistory.length - 1) {
                    this.historyIndex++;
                    this.input.value = this.commandHistory[this.historyIndex];
                } else {
                    this.historyIndex = this.commandHistory.length;
                    this.input.value = '';
                }
            }
        });
    }
    
    registerCommands() {
        this.commands = {
            help: {
                description: 'Show all available commands',
                execute: () => {
                    this.log('=== Debug Console Commands ===');
                    Object.keys(this.commands).forEach(cmd => {
                        this.log(`${cmd} - ${this.commands[cmd].description}`);
                    });
                }
            },
            
            godmode: {
                description: 'Toggle god mode (invincibility)',
                execute: () => {
                    this.godMode = !this.godMode;
                    this.game.player.invulnerable = this.godMode;
                    this.log(`God mode: ${this.godMode ? 'ON' : 'OFF'}`);
                }
            },
            
            essence: {
                description: 'essence N - Give yourself N essence',
                execute: (args) => {
                    const amount = parseInt(args[0]);
                    if (isNaN(amount)) {
                        this.log('Usage: essence <amount>');
                        return;
                    }
                    this.game.player.addEssence(amount);
                    this.log(`Added ${amount} essence. Total: ${this.game.player.essence}`);
                }
            },
            
            spawn: {
                description: 'spawn <type> - Spawn entity in front of player (sasquatch, chooma, shark, greatwhite, mike, clarence, lez)',
                execute: (args) => {
                    const type = args[0]?.toLowerCase();
                    if (!type) {
                        this.log('Usage: spawn <type>');
                        this.log('Types: sasquatch, chooma, shark, greatwhite, mike, clarence, lez');
                        return;
                    }
                    
                    // Calculate spawn position in front of player
                    const spawnDistance = 10;
                    const forward = new THREE.Vector3();
                    this.game.camera.getWorldDirection(forward);
                    forward.y = 0;
                    forward.normalize();
                    
                    const spawnPos = this.game.player.mesh.position.clone();
                    spawnPos.add(forward.multiplyScalar(spawnDistance));
                    spawnPos.y = this.game.world.getTerrainHeightAt(spawnPos);
                    
                    switch (type) {
                        case 'sasquatch':
                            this.game.enemyManager.spawnEnemy(spawnPos);
                            this.log(`Spawned Sasquatch at ${spawnPos.x.toFixed(1)}, ${spawnPos.z.toFixed(1)}`);
                            break;
                            
                        case 'chooma':
                            if (this.game.choomaManager) {
                                this.game.choomaManager.spawnChooma(spawnPos);
                                this.log(`Spawned Chooma at ${spawnPos.x.toFixed(1)}, ${spawnPos.z.toFixed(1)}`);
                            }
                            break;
                            
                        case 'shark':
                            if (this.game.sharkManager) {
                                this.game.sharkManager.spawnShark(spawnPos, 'normal');
                                this.log(`Spawned Shark at ${spawnPos.x.toFixed(1)}, ${spawnPos.z.toFixed(1)}`);
                            }
                            break;
                            
                        case 'greatwhite':
                            if (this.game.sharkManager) {
                                this.game.sharkManager.spawnShark(spawnPos, 'greatwhite');
                                this.log(`Spawned Great White Shark at ${spawnPos.x.toFixed(1)}, ${spawnPos.z.toFixed(1)}`);
                            }
                            break;
                            
                        case 'mike':
                            if (this.game.bossManager) {
                                this.game.bossManager.spawnBoss('mike', spawnPos);
                                this.log(`Spawned Mike Nolan at ${spawnPos.x.toFixed(1)}, ${spawnPos.z.toFixed(1)}`);
                            }
                            break;
                            
                        case 'clarence':
                            if (this.game.bossManager) {
                                this.game.bossManager.spawnBoss('clarence', spawnPos);
                                this.log(`Spawned Clarence at ${spawnPos.x.toFixed(1)}, ${spawnPos.z.toFixed(1)}`);
                            }
                            break;
                            
                        case 'lez':
                            if (this.game.bossManager) {
                                this.game.bossManager.spawnBoss('lez', spawnPos);
                                this.log(`Spawned Lez at ${spawnPos.x.toFixed(1)}, ${spawnPos.z.toFixed(1)}`);
                            }
                            break;
                            
                        default:
                            this.log(`Unknown entity type: ${type}`);
                            this.log('Types: sasquatch, chooma, shark, greatwhite, mike, clarence, lez');
                    }
                }
            },
            
            clear: {
                description: 'Clear console output',
                execute: () => {
                    this.output.innerHTML = '';
                }
            },
            
            kill: {
                description: 'Kill all enemies on screen',
                execute: () => {
                    let count = 0;
                    this.game.enemyManager.enemies.forEach(enemy => {
                        if (!enemy.isDead) {
                            enemy.takeDamage(999999);
                            count++;
                        }
                    });
                    this.log(`Killed ${count} enemies`);
                }
            },
            
            tp: {
                description: 'tp <x> <y> <z> - Teleport to coordinates',
                execute: (args) => {
                    const x = parseFloat(args[0]);
                    const y = parseFloat(args[1]);
                    const z = parseFloat(args[2]);
                    
                    if (isNaN(x) || isNaN(y) || isNaN(z)) {
                        this.log('Usage: tp <x> <y> <z>');
                        return;
                    }
                    
                    this.game.player.camera.position.set(x, y, z);
                    this.game.player.mesh.position.set(x, y, z);
                    this.log(`Teleported to ${x}, ${y}, ${z}`);
                }
            }
        };
    }
    
    toggle() {
        this.isVisible = !this.isVisible;
        this.container.style.display = this.isVisible ? 'block' : 'none';
        
        if (this.isVisible) {
            this.input.focus();
            // Unlock pointer controls when console is open
            if (this.game.controls && this.game.controls.isLocked) {
                this.game.controls.unlock();
            }
        }
    }
    
    executeCommand(commandString) {
        this.log(`> ${commandString}`, '#00ffff');
        
        const parts = commandString.split(' ');
        const cmdName = parts[0].toLowerCase();
        const args = parts.slice(1);
        
        const command = this.commands[cmdName];
        if (command) {
            try {
                command.execute(args);
            } catch (error) {
                this.log(`Error: ${error.message}`, '#ff0000');
            }
        } else {
            this.log(`Unknown command: ${cmdName}. Type "help" for commands.`, '#ff0000');
        }
    }
    
    log(message, color = '#00ff00') {
        const line = document.createElement('div');
        line.style.color = color;
        line.textContent = message;
        this.output.appendChild(line);
        this.output.scrollTop = this.output.scrollHeight;
    }
}
